import { t } from "i18next";
import { useState } from "react";
import type { NavigateFunction } from "react-router-dom";
import { Pencil, Trash2 } from "lucide-react";

import { RowActionsMenu } from "@/core/components/shared/data-table";
import { MutationConfirmPreview } from "@/core/components/shared/preview-action";
import { notifSilence, notifSilenceError } from "@/core/utils/func/notifApp";
import type { NotificationProviderDto } from "@/features/notification/api/dto";
import { useDeleteNotificationProvider } from "@/features/notification/api/use-delete-notification-provider";
import { NotificationProviderFormDialog } from "@/features/notification/components/providers/notification-provider-form-dialog";

interface Props {
  provider: NotificationProviderDto;
  navigate: NavigateFunction;
}

/**
 * Actions de ligne pour un `NotificationProvider` : édition (ouvre le form
 * dialog) ou suppression sous `MutationConfirmPreview`.
 */
export function NotificationProviderRowActions({ provider }: Props) {
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const del = useDeleteNotificationProvider();

  return (
    <>
      <RowActionsMenu
        actions={[
          {
            id: "edit",
            label: t("notifications.providers.rowActions.edit"),
            icon: Pencil,
            onClick: () => setEditOpen(true),
          },
          {
            id: "delete",
            label: t("notifications.providers.rowActions.delete"),
            icon: Trash2,
            variant: "destructive",
            onClick: () => setDeleteOpen(true),
          },
        ]}
      />

      <NotificationProviderFormDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        provider={provider}
      />

      <MutationConfirmPreview
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        data={{
          id: provider.id,
          code: provider.code,
          channel: provider.channel,
          isActive: provider.isActive,
        }}
        title={t("notifications.providers.preview.deleteTitle")}
        description={t("notifications.providers.preview.deleteDescription")}
        confirmLabel={t("notifications.providers.rowActions.delete")}
        t={t}
        pending={del.isPending}
        onConfirm={() => {
          del.mutate(provider.id, {
            onSuccess: () => {
              notifSilence(t("notifications.providers.feedback.deleted"));
              setDeleteOpen(false);
            },
            onError: (error) => notifSilenceError(error.message),
          });
        }}
      />
    </>
  );
}
